/* eslint-disable jsx-a11y/interactive-supports-focus */
/** @jsxImportSource @emotion/react */
import { Box, FlexLayout, H3, Icon } from '@deity/falcon-ui';
import type { IPropertyBag } from '@deity-io/di-data-basics';
import { Input } from '@deity-io/portal-ui';
import type { ISourceModel, SchemaActions } from 'contexts/SchemaContext';
import { SchemaActionTypes } from 'contexts/SchemaContext';
import { CACHE_FIRST, META_KEY } from 'contracts/constants';
import type { ICollectionType, IField } from 'contracts/interfaces';
import type { FC } from 'react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { useGetModelQuery } from 'services/graphql/generated/hooks';
import { typeGuard } from 'utils';
import { isCollection } from 'utils/isCollection';
import AddIcon from './icons/add.svg';
import ChevronDownIcon from './icons/chevron-down.svg';
import ChevronRightIcon from './icons/chevron-right.svg';
import ChildIcon from './icons/child.svg';
import DeleteIcon from './icons/delete.svg';
import HandelIcon from './icons/handel.svg';

interface ISchemaFieldProps {
  isEditable: boolean;
  path: string[];
  field: string; // name of the current key
  selectedField: string[];
  schema: ICollectionType | IField;
  parentSchema?: ICollectionType; // only used to check if dropped on current parent
  onAction?: (action: SchemaActions) => void;
  onSelect?: (path: Array<string>, source?: ISourceModel) => void;
  onToggleChecked?: (path: Array<string>, checked: boolean) => void;
  validateKey?: (path: Array<string>, key: string) => boolean;
  checkedFields?: Array<string>;
  packageVersionUuid?: string;
  source?: ISourceModel;
  typeNameAdornments?: [any];
  isLinkReplacement?: boolean;
}
const SchemaField: React.FC<ISchemaFieldProps> = ({
  isEditable,
  path,
  field,
  selectedField,
  schema,
  parentSchema,
  checkedFields,
  onAction,
  onSelect,
  validateKey,
  onToggleChecked,
  packageVersionUuid,
  source,
  typeNameAdornments,
  isLinkReplacement
}) => {
  const [isCollapsed, setCollapsed] = useState(false);
  const [isInEdit, setIsInEdit] = useState(false);
  const [isInError, setIsInError] = useState(false);
  const schemaFieldRef = useRef<HTMLDivElement>(null);
  const [accept, setAccept] = useState<string[]>([]);

  const type = typeGuard(schema);
  const hasChildren = (type === 'dict' || type === 'list') && Object.keys(schema).filter(key => key !== '_meta').length;
  const isRoot = path.length === 0;
  const isSelected = path.toString() === selectedField.toString();
  const isRequired = (input: IField | ICollectionType): boolean => {
    const config = isCollection(input) ? input._meta : input;

    if (config && typeof config === 'object') {
      return (config as IPropertyBag).required === false;
    }

    return true;
  };

  const checkPath = `/${path.join('/')}`;
  const isChecked = onToggleChecked && checkedFields && checkedFields.find(item => item === checkPath) !== undefined;

  const [{ style, isDragging }, drag, dragPreview] = useDrag(
    () => ({
      type: 'node',
      item: { path },
      canDrop: () => {
        // eslint-disable-next-line no-console
        console.log('drag, canDrop');

        return true;
      },
      collect: monitor => ({
        style: {
          opacity: monitor.isDragging() ? 0.3 : 1,
          visibility: monitor.isDragging() ? 'visible' : 'hidden'
        },
        isDragging: monitor.isDragging()
      })
    }),
    [path]
  );

  const isLink = type === 'link';

  const addNewField = e => {
    e.stopPropagation();
    if (onAction) {
      onAction({
        type: SchemaActionTypes.ADD_NEW_FIELD,
        payload: { fieldPath: path }
      });
    }
  };

  const onDrop = (item: any, monitor: any) => {
    // eslint-disable-next-line no-console
    console.log('onDRop ');
    if (isSameFieldOrRelated(item, path)) {
      // eslint-disable-next-line no-console
      console.log('onDRop sameField');

      return;
    }
    if (onAction) {
      if (isCollection(schema)) {
        // eslint-disable-next-line no-console
        console.log('onDRop ADD Child');
        onAction({
          type: SchemaActionTypes.ADD_CHILD,
          payload: { sPath: item.path, tPath: path }
        });
      } else {
        // eslint-disable-next-line no-console
        console.log('onDRop Switch order');
        onAction({
          type: SchemaActionTypes.SWITCH_ORDER,
          payload: { sPath: item.path, tPath: path }
        });
      }
    }
  };

  const onDropOrder = (item: any, monitor: any) => {
    if (isSameFieldOrRelated(item, path)) {
      // eslint-disable-next-line no-console
      console.log('onDropOrder sameField');

      return;
    }
    if (onAction) {
      onAction({
        type: SchemaActionTypes.SWITCH_ORDER,
        payload: { sPath: item.path, tPath: path }
      });
    }
  };

  const isSameFieldOrRelated = (item: any, fieldPath: string[]) => {
    let isRelated: boolean = false;
    const pathCopy = [...fieldPath];
    if (path.toString() === item.path.toString()) {
      return true;
    }
    if (item.path.length < path.length) {
      isRelated = path.some((p: any, i: number) => {
        pathCopy.pop();

        return pathCopy?.toString() === item.path.toString();
      });
    }

    return isRelated;
  };
  /** Drop on a collection */
  const [{ isOver, canDrop, dropItemPath }, drop] = useDrop({
    accept, // 'node',  if its node, we accept all events, may need it to handle onOverDrop on the row it self
    drop: (item: any, monitor) => onDrop(item, monitor),
    canDrop: () => {
      if (dropItemPath === null) {
        return false;
      }
      if (`${dropItemPath.path}` === `${path}`) {
        return false;
      }
      if (`${dropItemPath.path.slice(0, -1)}` === `${path}`) {
        return true;
      }

      if (dropItemPath.path.length) {
        const dragName = dropItemPath.path[dropItemPath.path.length - 1];
        if (dragName in schema) {
          // console.log(`can NOT drop ${dropItemPath?.path} on ${path}`, dragName, type);
          return false;
        }
      }

      return true;
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
      dropItemPath: monitor.getItem()
    })
  });

  /** Drop on the blue div between children of a collection 2 */
  const [{ isOverOnOrder, canDropOnOrder, onOrderItemPath }, dropOnOrder] = useDrop({
    accept: !isRoot ? 'node' : [],
    drop: (item: any, monitor) => onDropOrder(item, monitor),
    canDrop: () => {
      if (onOrderItemPath === null) {
        return false;
      }
      if (`${onOrderItemPath.path}` === `${path}`) {
        return false;
      }
      if (`${onOrderItemPath.path.slice(0, -1)}` === `${path.slice(0, -1)}`) {
        return true;
      }

      if (onOrderItemPath && onOrderItemPath.path.length) {
        const dragName = onOrderItemPath.path[onOrderItemPath.path.length - 1];
        if (parentSchema && dragName in parentSchema) {
          // console.log(`can NOT drop onOver ${onOrderItemPath?.path} on ${path}`, dragName, type);
          return false;
        }
      }

      return true;
    },
    collect: monitor => ({
      isOverOnOrder: monitor.isOver(),
      canDropOnOrder: monitor.canDrop(),
      onOrderItemPath: monitor.getItem()
    })
  });

  const { data: linkedModelData, loading: linkedModelLoading } = useGetModelQuery({
    variables: {
      packageVersionUuid: packageVersionUuid || '',
      uuid: schema.link || ''
    },
    skip: !isLink || !packageVersionUuid || !schema.link,
    ...CACHE_FIRST
  });

  const isActive = isOver && canDrop;

  useEffect(() => {
    if (type === 'dict' || (type === 'list' && !isDragging)) {
      setAccept(['node']);
    } else {
      setAccept([]);
    }
  }, [isDragging, type]);

  if (!schema) {
    return null;
  }

  /**
   * Replace the link field with the new schema
   */
  if (isLink) {
    if (linkedModelLoading) {
      return (
        <div
          ref={schemaFieldRef}
          aria-hidden
          css={{
            boxSizing: 'border-box',
            position: 'relative',
            cursor: 'pointer'
          }}
          onClick={e => {
            e.stopPropagation();
            if (onSelect) {
              onSelect(path, source);
            }
          }}
        >
          <div
            ref={drop}
            style={{
              opacity: style.opacity,
              backgroundColor: `${isOver && !isDragging ? '#f4f5f7' : ''}`
            }}
            css={{
              padding: '0 4em',
              paddingRight: '0',
              border: 'none',
              borderBottom: '1px solid #E0E2E5',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transform: 'all .2s ease',
              borderTop: `${isRoot ? '1px solid #E0E2E5' : 'none'}`,
              backgroundColor: `${isSelected ? '#f3f4fb' : 'white'}`,
              ':hover': {
                backgroundColor: '#f3f4fb',
                '~ #add_bar': {
                  opacity: 1
                },
                '~ #tree_bar': {
                  backgroundColor: '#1e193c'
                }
              }
            }}
          >
            <div
              css={{
                height: '64px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingLeft: `${path.length * 1.5}em`
              }}
            >
              Loading...
            </div>
          </div>
        </div>
      );
    }
    const model = linkedModelData?.getModel;
    if (model) {
      return (
        <SchemaField
          key={[...path].join('.')}
          path={[...path]}
          field={field}
          isEditable={false}
          selectedField={selectedField}
          schema={model.schema as IField}
          parentSchema={schema as ICollectionType}
          onAction={onAction}
          onSelect={onSelect}
          onToggleChecked={onToggleChecked}
          validateKey={validateKey}
          checkedFields={checkedFields}
          packageVersionUuid={packageVersionUuid}
          source={{
            uuid: model.uuid,
            path: [...path],
            schema: model.schema,
            name: model.name,
            link: { modelUuid: schema.link as string, path: [...path] }
          }}
          isLinkReplacement
          typeNameAdornments={[
            <Icon key="myLinkArrow" src="link" fill="badgeText" stroke="transparent" css={{ width: 14 }} />
          ]}
        />
      );
    }
  }
  const myLink =
    isLink || isLinkReplacement
      ? { modelUuid: schema.link as string, path: [...path], schema: isLinkReplacement ? parentSchema : schema }
      : undefined;

  return (
    <div
      ref={schemaFieldRef}
      aria-hidden
      onClick={e => {
        e.stopPropagation();
        if (onSelect) {
          onSelect(path, { ...source, link: myLink } as ISourceModel);
        }
      }}
      css={{
        boxSizing: 'border-box',
        position: 'relative',
        cursor: 'pointer'
      }}
    >
      <div
        ref={drop}
        style={{
          opacity: style.opacity,
          backgroundColor: `${isOver && !isDragging ? '#f4f5f7' : ''}`
        }}
        css={{
          padding: '0 4em',
          paddingRight: '0',
          border: 'none',
          borderBottom: '1px solid #E0E2E5',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transform: 'all .2s ease',
          borderTop: `${isRoot ? '1px solid #E0E2E5' : 'none'}`,
          backgroundColor: `${isSelected ? '#f3f4fb' : 'white'}`,
          ':hover': {
            backgroundColor: '#f3f4fb',
            '~ #add_bar': {
              opacity: 1
            },
            '~ #tree_bar': {
              backgroundColor: '#1e193c'
            }
          }
        }}
      >
        <div
          css={{
            height: '64px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            paddingLeft: `${path.length * 1.5}em`
          }}
        >
          <Icon key="typeIcon" src={type} fallback="dict" fill="badgeText" stroke="transparent" css={{ width: 18 }} />

          {isSelected && isInEdit ? (
            <Input
              key="input"
              disabled={!isEditable}
              required
              error={isInError}
              type="text"
              name="name"
              id="name"
              defaultValue={field}
              autoComplete="off"
              autoFocus
              fullWidth
              inputProps={{
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '18px'
              }}
              onChange={e => {
                e.target.error = true;
                if (field !== e.target.value && validateKey) {
                  setIsInError(!validateKey(path, e.target.value));
                } else if (isInError) {
                  setIsInError(false);
                }
              }}
              // @ts-ignore
              onKeyPress={e => {
                if (e.key === 'Enter' && onAction) {
                  onAction({
                    type: SchemaActionTypes.UPDATE_NAME,
                    payload: { path, fieldName: e.target.value }
                  });
                }
              }}
              // @ts-ignore
              onKeyUp={e => {
                if (e.key === 'Escape') {
                  // @todo
                }
              }}
              onBlur={e => {
                if (onAction && field !== e.target.value) {
                  let allow = true;
                  if (validateKey) {
                    allow = validateKey(path, e.target.value);
                  }
                  if (allow) {
                    onAction({
                      type: SchemaActionTypes.UPDATE_NAME,
                      payload: { path, fieldName: e.target.value }
                    });
                  } else {
                    e.target.value = field;
                  }
                }
                setIsInEdit(false);
              }}
            />
          ) : (
            <p
              key="btnHolder"
              css={{
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 'normal',
                fontSize: '18px',
                lineHeight: '28px',
                marginLeft: '1em'
              }}
            >
              <a
                role="button"
                onClick={() => {
                  if (isSelected && isEditable) {
                    setIsInError(false);
                    setIsInEdit(true);
                  }
                }}
              >
                {field}
              </a>
            </p>
          )}
        </div>
        <div
          key="someFlex"
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <FlexLayout key="typeBadges" flexDirection="row" alignItems="center">
            {typeNameAdornments}
            <p
              css={{
                fontFamily: 'Inter',
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '14px',
                lineHeight: '18px',
                textAlign: 'right',
                marginLeft: '1em',
                textTransform: 'uppercase',
                color: '#BDC0D0'
              }}
            >
              {isRequired(schema) ? `? ${type}` : type}
            </p>
          </FlexLayout>
          <div
            key="delete"
            css={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              minWidth: '200px',
              justifyContent: 'space-around',
              marginLeft: '2em',
              cursor: 'pointer'
            }}
          >
            {isEditable && !isRoot && (
              <DeleteIcon
                onClick={e => {
                  e.stopPropagation();
                  if (onAction) {
                    onAction({
                      type: SchemaActionTypes.REMOVE_FIELD,
                      payload: { path }
                    });
                  }
                }}
              />
            )}
            <span
              key="collapse"
              style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
              aria-hidden
              onClick={e => {
                e.stopPropagation();
                setCollapsed(!isCollapsed);
              }}
            >
              {isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
            </span>
            {isEditable && (
              <span key="drag" ref={drag}>
                <HandelIcon
                  style={{ visibility: isRoot ? 'hidden' : 'visible' }}
                  css={{
                    cursor: 'move'
                  }}
                />
              </span>
            )}
            {onToggleChecked && (
              <input
                key="checkbox"
                type="checkbox"
                checked={isChecked}
                onChange={value => {
                  onToggleChecked([...path], value.target.checked);
                }}
              />
            )}
          </div>

          <FlexLayout
            key="otherFlex"
            css={{
              width: '32px',
              height: '64px',
              opacity: selectedField?.join('.') === path.join('.') ? 1 : 0,
              transition: 'all .2s ease-in-out'
            }}
            flexWrap="nowrap"
            alignItems="center"
            justifyContent="space-between"
          >
            <Icon
              key="icon"
              src="arrowLeftPolygon"
              fill="border"
              stroke="transparent"
              css={{ transform: 'translateX(5px)' }}
            />
            <Box key="box" bg="border" css={{ width: '3px', height: '100%' }} />
          </FlexLayout>
        </div>
      </div>
      {isEditable && (
        <div
          id="add_bar"
          key="add_bar"
          aria-hidden
          onClick={addNewField}
          className="the_root_node_add_bar"
          style={{
            display: (type === 'list' || type === 'dict') && !isDragging ? 'flex' : 'none'
          }}
          css={{
            cursor: 'pointer',
            width: '100%',
            top: '45px',
            position: 'absolute',
            left: `${1.5 * path.length + 3.6}em`,
            display: 'flex',
            alignItems: 'center',
            transform: 'translateY(10px)',
            transition: 'opacity .2s ease',
            zIndex: 101,
            opacity: 0,
            ':hover': {
              opacity: 1
            }
          }}
        >
          <AddIcon key="addIcon" />
          <div
            css={{
              height: '0.5em',
              backgroundColor: '#BDC0D0',
              width: '100%',
              transform: 'translateX(-1px)'
            }}
          />
        </div>
      )}
      <div
        key="drop"
        ref={dropOnOrder}
        style={{
          backgroundColor: canDropOnOrder ? '#a49bef' : '#f44336',
          opacity: isOverOnOrder && !isDragging ? '1' : '0'
        }}
        css={{
          width: '100%',
          position: 'absolute',
          zIndex: 105,
          height: '0.3em',
          top: '0',
          visibility: 'visible'
        }}
      />

      <div
        key="dragIcon"
        ref={dragPreview}
        css={{
          width: '40%',
          position: 'absolute',
          zIndex: 105,
          height: '3em',
          top: '0',
          backgroundColor: 'white',
          transform: 'translateX(-100%)',
          display: 'flex',
          alignItems: 'center',
          padding: '1em',
          boxSizing: 'border-box',
          border: '1px solid #E0E2E5',
          borderRadius: '2px'
        }}
      >
        <Icon src={type} fallback="dict" fill="badgeText" stroke="transparent" css={{ width: 18 }} />
        <H3 px="sm" py="sm">
          {isSelected ? <p>test</p> : field}
        </H3>
      </div>
      <div
        id="tree_bar"
        css={{
          position: 'absolute',
          width: '1px',
          backgroundColor: '#E0E2E5',
          left: `${1.5 * (path.length + 3.2)}em`,
          height: 'calc(100% - 65px)',
          transition: 'all .2s ease',
          zIndex: 99
        }}
      />
      {isOver && canDrop && `_meta` in schema && !isDragging && <MakeChildNotification parentName={field} />}

      <div
        key="mainDiv"
        style={{ overflowY: isCollapsed ? 'hidden' : 'visible' }}
        css={{
          height: `${isCollapsed ? '0px' : 'auto'}`
        }}
      >
        {schema &&
          '_meta' in schema &&
          Object.keys(schema).map(key => {
            if (key === META_KEY) {
              return null;
            }

            return (
              <SchemaField
                key={[...path, key].join('.')}
                path={[...path, key]}
                field={key}
                isEditable={isEditable}
                selectedField={selectedField}
                schema={schema[key] as IField}
                parentSchema={schema as ICollectionType}
                onAction={onAction}
                onSelect={onSelect}
                onToggleChecked={onToggleChecked}
                validateKey={validateKey}
                checkedFields={checkedFields}
                packageVersionUuid={packageVersionUuid}
                source={source}
              />
            );
          })}
      </div>
    </div>
  );
};

export default SchemaField;

interface MakeChildNotificationProps {
  parentName: string;
}
const MakeChildNotification: FC<MakeChildNotificationProps> = ({ parentName }) => (
  <Box>
    <Box
      css={{
        position: 'absolute',
        left: '0',
        top: '52px',
        zIndex: 300,
        backgroundColor: '#BDC0D0',
        minWidth: '200px',
        height: '33px',
        borderRadius: '2em',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '1em',
        paddingRight: '1em'
      }}
    >
      <ChildIcon />
      <h3
        css={{
          color: 'white',
          marginLeft: '.5em',
          marginRight: '.5em',
          fontSize: '14px'
        }}
      >
        CHILD OF
      </h3>
      <H3
        fontWeight="bold"
        css={{
          textTransform: 'uppercase',
          fontSize: '14px'
        }}
      >
        {parentName}
      </H3>
    </Box>
    <Box
      width="full"
      css={{
        position: 'absolute',
        height: '9px',
        zIndex: 200,
        backgroundColor: '#BDC0D0'
      }}
    />
  </Box>
);
